import { useState } from "react";
import { MapPin, X, Navigation, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNearbyAirport } from "@/hooks/useNearbyAirport";
import { Link } from "react-router-dom";

export const NearbyAirportBanner = () => {
  const { isSupported, nearbyAirport, isLocating, requestLocation } = useNearbyAirport();
  const [dismissed, setDismissed] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  if (dismissed || !isSupported) return null;

  // Show the detected airport
  if (nearbyAirport) {
    return (
      <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-center gap-3 animate-in slide-in-from-top-2 duration-300">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
          <MapPin className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            You're near {nearbyAirport.name} ({nearbyAirport.code})
          </p>
          <p className="text-xs text-muted-foreground">
            {nearbyAirport.distanceMiles} miles away
          </p>
        </div>
        <Link to={`/airports/${nearbyAirport.code.toLowerCase()}`}>
          <Button size="sm" className="active:scale-95 transition-transform">
            View Wait Times
          </Button>
        </Link>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Show the prompt to detect location
  if (!hasRequested) {
    return (
      <div className="bg-muted/50 border border-border rounded-xl p-4 flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
          <Navigation className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">At an airport?</p>
          <p className="text-xs text-muted-foreground">
            We can detect your nearest airport automatically
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={isLocating}
          className="active:scale-95 transition-transform"
          onClick={async () => {
            setHasRequested(true);
            const result = await requestLocation();
            if (!result) {
              // No airport found nearby, dismiss
              setTimeout(() => setDismissed(true), 3000);
            }
          }}
        >
          {isLocating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <MapPin className="h-4 w-4 mr-1" />
              Detect
            </>
          )}
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Searching state with no result yet
  if (isLocating) {
    return (
      <div className="bg-muted/50 border border-border rounded-xl p-4 flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Detecting nearby airports...</p>
      </div>
    );
  }

  // No airport found
  return (
    <div className="bg-muted/50 border border-border rounded-xl p-3 flex items-center gap-3 animate-in fade-in duration-300">
      <MapPin className="h-4 w-4 text-muted-foreground" />
      <p className="text-xs text-muted-foreground">No airports detected nearby</p>
      <button
        onClick={() => setDismissed(true)}
        className="ml-auto p-1 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};
